import i18n from "@bcc/lib/i18n";
import { calculateMarginPercent } from "@bcc/lib/products/cross-border-tax";
import { SkuRegionPriceSchema } from "@shared/schemas/enums/crossBorder.enum";
import { parse, startOfDay } from "date-fns";
import { z } from "zod";

const V = "views.shopifyImportModal.productForm.validation" as const;
const L = "views.shopifyImportModal.productForm.variants.labels" as const;

export interface VariantSchemaConfig {
  shortTermMinWeeklyInventory?: number;
  shortTermMaxWeeklyInventory?: number;
  longTermMinWeeklyInventory?: number;
  longTermMaxWeeklyInventory?: number;
  enforceMarginFloor?: boolean;
  marginFloorPercent?: number;
}

const variantBaseObject = z.object({
  id: z.string(),
  // Unselected Shopify variants can arrive without a detected bagSize (e.g. an
  // ounce-based variant on a metric-only vendor config). Mirror the parent
  // `Variant.bagSize?: string` shape so the entire array doesn't fail base
  // validation; the superRefine below still requires bagSize for variants with
  // isProductSelectedForImport === true.
  bagSize: z.string().optional(),
  grind: z.array(z.string()),
  // Per-bag-size cross-border ship-to (";"-separated, e.g. "NL;DE"). Entered on
  // the whole-bean variant and inherited by other grinds of the same bag size.
  // Optional: empty for non-cross-border vendors; the home country is always
  // added and locked-on by the UI, so a cross-border SKU is never empty.
  ship_to: z.string().nullish(),
  price: z.union([z.string(), z.number()]),
  wholesalePrice: z.union([z.string(), z.number()]),
  term: z.enum(["Short", "Long"], {
    message: i18n.t(`${V}.termRequired`),
  }),
  startDate: z.string(),
  endDate: z.string().nullish(),
  weeklyInventory: z.number().nullish(),
  totalMaxShortTermInventory: z.number().nullish(),
  isProductSelectedForImport: z.boolean(),
  // Optional fields from FormVariant interface
  localGrindOverride: z.array(z.string()).optional(),
  mapDetectedBagSizeTo: z.string().optional(),
  hasDetectedBagSize: z.boolean().optional(),
  hasDetectedGrindType: z.boolean().optional(),
  hasValidDetectedBagSize: z.boolean().optional(),
  hasValidDetectedGrindType: z.boolean().optional(),
  isImportedRemote: z.boolean().optional(),
  hasDetectedGrindOverride: z.boolean().optional(),
  hasDetectedBagSizeOverride: z.boolean().optional(),
  isImportedLocalVariant: z.boolean().optional(),
  index: z.number().optional(),
  status: z.string().optional(),
  isBagSizeVariantImported: z.boolean().optional(),
  serviceId: z.string().optional(),
  isVariantHasSameBagSize: z.boolean().optional(),
  detectedGrindInfo: z
    .array(
      z.object({
        grind: z.string(),
        mapDetectedGrindTo: z.string().optional(),
        isValidGrind: z.boolean(),
      })
    )
    .optional(),
  // Cross-border bruto-entry toggles + the derived per-region prices computed
  // from the home-region entry. Persisted into the product target metadata.
  vatIncluded: z.boolean().optional(),
  ksIncluded: z.boolean().optional(),
  skuPrices: SkuRegionPriceSchema.array().optional(),
});

type VariantInput = z.infer<typeof variantBaseObject>;
type VariantRefinementCtx = z.core.$RefinementCtx<VariantInput>;

const validateGrindAndBagSize = (variant: VariantInput, ctx: VariantRefinementCtx) => {
  if (variant.grind.length === 0) {
    ctx.addIssue({
      code: "custom",
      message: i18n.t(`${V}.required`, { field: i18n.t(`${L}.grind`) }),
      path: ["grind"],
    });
  }

  if (!variant.bagSize || variant.bagSize === "") {
    ctx.addIssue({
      code: "custom",
      message: i18n.t(`${V}.required`, { field: i18n.t(`${L}.bagSize`) }),
      path: ["bagSize"],
    });
  }
};

// Margin guard — both `enforceMarginFloor` and `marginFloorPercent` must be
// set. `enforceMarginFloor` is the opt-in gate; `marginFloorPercent` is the
// per-region threshold from SSM (RegionMarginConfig.marginPercentage).
const validateMarginFloor = (
  variant: VariantInput,
  ctx: VariantRefinementCtx,
  { enforceMarginFloor, marginFloorPercent }: Pick<VariantSchemaConfig, "enforceMarginFloor" | "marginFloorPercent">
) => {
  if (!enforceMarginFloor || !marginFloorPercent || marginFloorPercent <= 0) {
    return;
  }

  const msrp = typeof variant.price === "number" ? variant.price : Number.parseFloat(String(variant.price));
  const wholesale = typeof variant.wholesalePrice === "number" ? variant.wholesalePrice : Number.parseFloat(String(variant.wholesalePrice));
  if (!Number.isFinite(msrp) || !Number.isFinite(wholesale) || msrp <= 0 || wholesale <= 0) {
    return;
  }

  const margin = calculateMarginPercent(msrp, wholesale);
  if (margin < marginFloorPercent) {
    ctx.addIssue({
      code: "custom",
      message: i18n.t(`${V}.marginTooLow`, { percent: marginFloorPercent, current: margin.toFixed(1) }),
      path: ["wholesalePrice"],
    });
  }
};

const validatePricing = (
  variant: VariantInput,
  ctx: VariantRefinementCtx,
  marginConfig: Pick<VariantSchemaConfig, "enforceMarginFloor" | "marginFloorPercent">
) => {
  if (!variant.price || variant.price === "") {
    ctx.addIssue({
      code: "custom",
      message: i18n.t(`${V}.required`, { field: i18n.t(`${L}.price`) }),
      path: ["price"],
    });
  }

  if (!variant.wholesalePrice || variant.wholesalePrice === "") {
    ctx.addIssue({
      code: "custom",
      message: i18n.t(`${V}.required`, {
        field: i18n.t(`${L}.wholesalePrice`),
      }),
      path: ["wholesalePrice"],
    });
  }

  validateMarginFloor(variant, ctx, marginConfig);
};

const validateStartDate = (variant: VariantInput, ctx: VariantRefinementCtx) => {
  if (!variant.startDate || variant.startDate === "") {
    ctx.addIssue({
      code: "custom",
      message: i18n.t(`${V}.required`, { field: i18n.t(`${L}.startDate`) }),
      path: ["startDate"],
    });
    return;
  }

  const parsedDate = parse(variant.startDate, "yyyy-MM-dd", new Date());
  if (!Number.isNaN(parsedDate.getTime()) && parsedDate < startOfDay(new Date())) {
    ctx.addIssue({
      code: "custom",
      message: i18n.t(`${V}.startDateInPast`),
      path: ["startDate"],
    });
  }
};

const validateWeeklyInventory = (variant: VariantInput, ctx: VariantRefinementCtx, min: number, max: number) => {
  if (variant.weeklyInventory == null) {
    ctx.addIssue({
      code: "custom",
      message: i18n.t(`${V}.required`, {
        field: i18n.t(`${L}.weeklyInventory`),
      }),
      path: ["weeklyInventory"],
    });
    return;
  }

  if (variant.weeklyInventory < min || variant.weeklyInventory > max) {
    ctx.addIssue({
      code: "custom",
      message: i18n.t(`${V}.inventoryRange`, { min, max }),
      path: ["weeklyInventory"],
    });
  }
};

const validateShortTermFields = (variant: VariantInput, ctx: VariantRefinementCtx) => {
  if (variant.term !== "Short") {
    return;
  }

  // endDate required for Short term
  if (!variant.endDate) {
    ctx.addIssue({
      code: "custom",
      message: i18n.t(`${V}.endDateRequired`),
      path: ["endDate"],
    });
  }

  // totalMaxShortTermInventory required and > 0 for Short term
  if (variant.totalMaxShortTermInventory == null) {
    ctx.addIssue({
      code: "custom",
      message: i18n.t(`${V}.required`, {
        field: i18n.t(`${L}.totalMaxShortTermInventory`),
      }),
      path: ["totalMaxShortTermInventory"],
    });
  } else if (variant.totalMaxShortTermInventory <= 0) {
    ctx.addIssue({
      code: "custom",
      message: i18n.t(`${V}.totalInventoryPositive`),
      path: ["totalMaxShortTermInventory"],
    });
  }
};

export const createVariantSchema = ({
  shortTermMinWeeklyInventory = 50,
  shortTermMaxWeeklyInventory = 200,
  longTermMinWeeklyInventory = 50,
  longTermMaxWeeklyInventory = 200,
  enforceMarginFloor = true,
  marginFloorPercent,
}: VariantSchemaConfig = {}) =>
  variantBaseObject.superRefine((variant, ctx) => {
    // Skip validation if not selected for import
    if (!variant.isProductSelectedForImport) {
      return;
    }

    validateGrindAndBagSize(variant, ctx);
    validatePricing(variant, ctx, { enforceMarginFloor, marginFloorPercent });
    validateStartDate(variant, ctx);

    const weeklyInventoryMin = variant.term === "Short" ? shortTermMinWeeklyInventory : longTermMinWeeklyInventory;
    const weeklyInventoryMax = variant.term === "Short" ? shortTermMaxWeeklyInventory : longTermMaxWeeklyInventory;
    validateWeeklyInventory(variant, ctx, weeklyInventoryMin, weeklyInventoryMax);

    validateShortTermFields(variant, ctx);
  });

// Default schema with standard inventory bounds — use createVariantSchema for config-driven bounds
export const variantSchema = createVariantSchema();
